import { generatePath } from "react-router-dom";

import fetcher_v2 from "@/api/fetcher_v2";
import { URLS } from "@/api/urls";
import { SearchSubscriptionInterval } from "@/constants";
import { Capabilities } from "@/types/Capabilities";
import { Organization, OrganizationLight } from "@/types/Organization";
import { ReportListItem } from "@/types/Report";
import { ReportSubscription } from "@/types/ReportSubscription";
import { SearchSubscription } from "@/types/SearchSubscription";
import { User } from "@/types/User";
import { OrganizationUsage } from "@/types/Usage";

export const getCapabilities = (uuid: Uuid) => {
  return fetcher_v2<Capabilities>(
    generatePath(URLS.ORGANIZATION_CAPABILITIES, { uuid })
  );
};

export const getOrganizationUsers = async (
  uuid: Uuid,
  query?: { ordering?: "last_login" | "-last_login" }
) => {
  return fetcher_v2<User[]>(generatePath(URLS.ORGANIZATION_USERS, { uuid }), {
    method: "GET",
    body: query,
  });
};

export const getOrganization = async () => {
  return fetcher_v2<Organization[]>(generatePath(URLS.ORGANIZATIONS), {
    method: "GET",
  });
};

export const getCurrentOrganizationUsage = async () => {
  return fetcher_v2<OrganizationUsage>(URLS.ORGANIZATION_USAGE);
};

type OrganizationUserParams = {
  user_id: "me" | number;
  organization_uuid: Uuid;
};

type OrganizationUserParamsWithBody = OrganizationUserParams & {
  user: Partial<Omit<User, "default_organization">>;
};

export const deleteOrganizationUser = ({
  organization_uuid,
  user_id,
}: OrganizationUserParams) => {
  return fetcher_v2<void>(
    generatePath(URLS.ORGANIZATION_USER, {
      uuid: organization_uuid,
      user_id,
    }),
    {
      method: "DELETE",
    }
  );
};

export const patchOrganizationUser = ({
  organization_uuid,
  user_id,
  user,
}: OrganizationUserParamsWithBody) => {
  return fetcher_v2<Omit<User, "default_organization">>(
    generatePath(URLS.ORGANIZATION_USER, {
      uuid: organization_uuid,
      user_id,
    }),
    {
      body: user,
      method: "PATCH",
    }
  );
};

export type NewUser = Pick<User, "email" | "user_type"> &
  Partial<Pick<User, "name" | "language">>;

type CreateOrganizationUserParams = {
  organization_uuid: string;
  user: NewUser;
};

export const createOrganizationUser = ({
  organization_uuid,
  user,
}: CreateOrganizationUserParams) => {
  return fetcher_v2<User, NewUser, { [key: string]: string[] }>(
    generatePath(URLS.ORGANIZATION_USERS, {
      uuid: organization_uuid,
    }),
    {
      body: user,
      method: "POST",
    }
  );
};

export const getOrganizationUser = async ({
  organization_uuid,
  user_id,
}: OrganizationUserParams) => {
  return fetcher_v2<User>(
    generatePath(URLS.ORGANIZATION_USER, {
      uuid: organization_uuid,
      user_id,
    })
  );
};

export const getOrganizationUserReportSubscriptions = ({
  organization_uuid,
  user_id,
}: OrganizationUserParams) => {
  return fetcher_v2<{ reports: ReportSubscription[] }>(
    generatePath(URLS.ORGANIZATION_USER_REPORT_SUBSCRIPTIONS, {
      uuid: organization_uuid,
      user_id,
    })
  );
};

export const getOrganizationUserSearchSubscriptions = ({
  organization_uuid,
  user_id,
}: OrganizationUserParams) => {
  return fetcher_v2<{ subscriptions: SearchSubscription[] }>(
    generatePath(URLS.ORGANIZATION_USER_SEARCH_SUBSCRIPTIONS, {
      uuid: organization_uuid,
      user_id,
    })
  );
};

export const editUserSearchSubscription = ({
  user_id,
  organization_uuid,
  sub_id,
  interval,
}: OrganizationUserParams & {
  sub_id: number;
  interval: SearchSubscriptionInterval;
}) => {
  return fetcher_v2(
    generatePath(URLS.ORGANIZATION_USER_SEARCH_SUBSCRIPTIONS_EDIT, {
      uuid: organization_uuid,
      user_id,
      sub_id,
    }),
    {
      method: "PATCH",
      body: {
        interval,
      },
    }
  );
};
type OrganizationUserReportParams = {
  report_uuid: string;
} & OrganizationUserParams;

export const addReportSubscription = ({
  organization_uuid,
  user_id,
  report_uuid,
}: OrganizationUserReportParams) => {
  return fetcher_v2<ReportSubscription>(
    generatePath(URLS.ORGANIZATION_USER_REPORT_SUBSCRIPTIONS_ADD, {
      uuid: organization_uuid,
      user_id,
    }),
    {
      body: { report_uuid },
      method: "POST",
    }
  );
};

export const deleteReportSubscription = ({
  organization_uuid,
  user_id,
  report_uuid,
  sub_id,
}: OrganizationUserReportParams & { sub_id: number }) => {
  return fetcher_v2(
    generatePath(URLS.ORGANIZATION_USER_REPORT_SUBSCRIPTIONS_DELETE, {
      uuid: organization_uuid,
      user_id,
      report_uuid,
      sub_id,
    }),
    {
      method: "DELETE",
    }
  );
};

export const getOrganizationReports = () => {
  return fetcher_v2<ReportListItem[]>(URLS.REPORT_LIST);
};

export const getOrganizations = () => {
  return fetcher_v2<OrganizationLight[]>(URLS.ORGANIZATION_LIST);
};

export const patchOrganization = (
  uuid: Uuid,
  organization: Partial<Organization>
) => {
  return fetcher_v2<Organization, Partial<Organization>>(
    generatePath(URLS.ORGANIZATIONS_DETAIL_V4, { uuid }),
    {
      method: "PATCH",
      body: organization,
    }
  );
};
export const deleteOrganization = (uuid: Uuid) => {
  return fetcher_v2<void, void, void>(
    generatePath(URLS.ORGANIZATIONS_DETAIL_V4, { uuid }),
    {
      method: "DELETE",
    }
  );
};
